import { Injectable } from '@angular/core';
import { NgForage, NgForageCache, CachedItem } from 'ngforage';
import { LoggerService } from '../logger/logger.service';
import { GridData } from '../../components/user-management/definitions/user-management.interface';

const defaultCacheTimeInMilliseconds: number = 1000 * 60 * 60;

@Injectable({
	providedIn: 'root'
})
export class LocalStorageService
{
	constructor(
		private readonly ngf: NgForage,
		private readonly cache: NgForageCache,
		private readonly loggerService: LoggerService
	) {}

	public striginifyJSON(value: unknown)
	{
		return JSON.stringify(value);
	}

	public parseJSON(value: string)
	{
		if (value === undefined)
		{
			return null;
		}
		return JSON.parse(value);
	}
	public isTokenCreated(): boolean
	{
		const items = { ...localStorage };
		return JSON.stringify(items) !== '{}';
	}

	public getItems<T = unknown>(key: string): Promise<T | null>
	{
		return this.ngf.getItem<T>(key);
	}

	public getCachedItem<T = unknown>(key: string): Promise<T | null>
	{
		return this.cache.getCached<T>(key).then((r: CachedItem<T>) =>
		{
			if (!r.hasData || r.expired)
			{
				return null;
			}

			return r.data;
		});
	}
	public setCached<T = unknown>(key: string, data: T, cacheTime: number = defaultCacheTimeInMilliseconds): Promise<T>
	{
		return this.cache.setCached(key, data, cacheTime);
	}

	public removeCached(key: string)
	{
		this.cache.removeCached(key);
	}

	public setItems<T = unknown>(key: string, data: T): Promise<T>
	{
		return this.ngf.setItem<T>(key, data);
	}

	public removeItem(key: string): Promise<void>
	{
		return this.ngf.removeItem(key);
	}
	public setLoggedInUser(value: string)
	{
		localStorage.setItem('userDetails', value);
	}


	public setLoggedInCorporateFlag(value: string)
	{
		localStorage.setItem('corporateAccountRequestFlag', value);
	}
	public getLoggedInUser()
	{
		if (localStorage.getItem('userDetails') !== null)
		{
			return localStorage.getItem('userDetails');
		}
		else
			return null;
	}
	getTmhCorporateAdminDetails()
	{
		if (localStorage.getItem('tmhCorporateAdminDetails') !== null)
		{
			return localStorage.getItem('tmhCorporateAdminDetails');
		}
		else
			return null;
	}
	setTmhCorporateAdminDetails(value: string)
	{
		localStorage.setItem('tmhCorporateAdminDetails', value);
	}
	getTmhCustomerSupportDetails()
	{
		if (localStorage.getItem('tmhCustomerSupportDetails') !== null)
		{
			return localStorage.getItem('tmhCustomerSupportDetails');
		}
		else
			return null;
	}
	setTmhCustomerSupportDetails(value: string)
	{
		localStorage.setItem('tmhCustomerSupportDetails', value);
	}
	public setLoggedInUserLocation(value: string)
	{
		localStorage.setItem('userDetailsLocation', value);
	}
	public getLoggedInUserLocation()
	{
		if (localStorage.getItem('userDetailsLocation') !== null)
		{
			return localStorage.getItem('userDetailsLocation');
		}
		else
			return null;
	}
	public setLanguage(value: string)
	{
		localStorage.setItem('language', value);
	}
	public getLanguage()
	{
		if (localStorage.getItem('language') !== null)
		{
			return localStorage.getItem('language');
		}
		else
			return null;
	}
	public setImpersonateUser(value: string)
	{
		localStorage.setItem('impersonateUser', value);
	}
	public removeImpersonateUser()
	{
		localStorage.removeItem('impersonateUser');
		localStorage.removeItem('userDetails');
	}
	public getImpersonateUser()
	{
		if (localStorage.getItem('impersonateUser') !== null)
		{
			return localStorage.getItem('impersonateUser');
		}
		else
			return null;
	}
	public setCustomerToDashboard(value: string)
	{
		localStorage.setItem('customerToDashboard', value);
	}
	public getCustomerToDashboard()
	{
		if (localStorage.getItem('customerToDashboard') !== null)
		{
			return localStorage.getItem('customerToDashboard') === 'true' ? true : false;
		}
		else
			return false;
	}
	public setRedirectedUser(value: string)
	{
		localStorage.setItem('redirectedEmail', value);
	}
	public getRedirectedUser()
	{
		
		return localStorage.getItem('redirectedEmail');
	}
	public removeRedirectedUser()
	{
		localStorage.removeItem('redirectedEmail');
	}
	public setRegistrationStatus(value: string)
	{
		localStorage.setItem('userStatus', value);
	}
	public getRegistrationStatus()
	{
		return localStorage.getItem('userStatus');
	}
	public setUserName(value: string)
	{
		localStorage.setItem('username', value);
	}
	public getUserName()
	{
		const userName = localStorage.getItem('username');
		if (userName !== null && userName !== '' )
		{
			return userName;
		}
		else
			return null;
	}
	public clearStorage()
	{
		localStorage.clear();
		sessionStorage.clear();
		this.ngf.clear()?.catch(error =>
		{
			this.loggerService.error(error);
		});;
		localStorage.removeItem('userDetails');
		localStorage.removeItem('userDetailsLocation');
		localStorage.removeItem('language');
		localStorage.removeItem('tmhCorporateAdminDetails');
		localStorage.removeItem('userStatus');
		localStorage.removeItem('headerAlertBanner');
		this.removeItem('new_business_account');
		this.removeItem('isTmhCorporateAdmin');
		this.removeItem('isPortalUser');
	}
	public setCustomerAemLabels(value: string)
	{
		localStorage.setItem('customerSupportLabels', value);
	}
	public getCustomerAemLabels()
	{
		return localStorage.getItem('customerSupportLabels');
	}
	public setAssignedUserRedirect(value: string)
	{
		localStorage.setItem('assignedUserRedirect', value);
	}
	public getAssignedUserRedirect()
	{
		return localStorage.getItem('assignedUserRedirect');
	}
	public removeAssignedUserRedirect() 
	{
		localStorage.removeItem('assignedUserRedirect');
	}
	public setAssignedLocationFromRedirect(value: string)
	{
		localStorage.setItem('assignedLocationFromRedirect', value);
	}
	public getAssignedLocationFromRedirect()
	{
		return localStorage.getItem('assignedLocationFromRedirect');
	}
	public removeAssignedLocationFromRedirect() 
	{
		localStorage.removeItem('assignedLocationFromRedirect');
	}
	public getCompanyLocationList()
	{
		return localStorage.getItem('companyLocationsList');
	}
	public setCompanyLocationList(value: GridData[])
	{	
		localStorage.setItem('companyLocationsList', JSON.stringify(value));
	}
	public removeCompanyLocationList()
	{
		return localStorage.removeItem('companyLocationsList');
	}
	public getIsMyInsightRedirect()
	{
		return localStorage.getItem('isMyInsightRedirect');
	}
	public setIsMyInsightRedirect(value: string)
	{
		return localStorage.setItem('isMyInsightRedirect', value);
	}
	public removeIsMyInsightRedirect()
	{
		return localStorage.removeItem('isMyInsightRedirect');
	}
	public setAssignedRoles(value: string)
	{
		localStorage.setItem('assignedRoles', value);
	}
	public getAssignedRoles()
	{
		return localStorage.getItem('assignedRoles');
	}
	public removeAssignedRoles()
	{
		localStorage.removeItem('assignedRoles');
	}
	public setCurrentRole(value: string)
	{
		localStorage.setItem('currentRole', value);
	}
	public getCurrentRole()
	{
		return localStorage.getItem('currentRole');
	}
	public removeCurrentRole()
	{
		localStorage.removeItem('currentRole');
	}
	public setSubscriptionLocationId(value: string)
	{
		localStorage.setItem('subscriptionLocationId', value);
	}
	public getSubscriptionLocationId()
	{
		return localStorage.getItem('subscriptionLocationId');
	}
}
